import { withScope } from '@sentry/react';

import {
	GQLResponseError,
	GQLResponsePerimeterXChallengeError,
	GQLResponseServerError,
} from '@change-corgi/core/gqlErrors';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type {
	PsfFacebookPostDataQuery,
	PsfFacebookPostDataQueryVariables,
	ShareRecommendationsStatus,
} from './facebookPostData.graphql';
import { PsfFacebookPostData } from './facebookPostData.graphql';

export type { PsfFacebookPostDataQueryVariables, ShareRecommendationsStatus } from './facebookPostData.graphql';

const FAILURE_MESSAGE = 'Failed to fetch PSF Facebook post sheet data';

export type FacebookGroupRecommendation = {
	readonly name: string;
	readonly url: string;
	readonly imageUrl: string | null;
};

const FACEBOOK_HOSTS = /(^|\.)(facebook|fb)\.com$/;

/**
 * A group card opens its URL with `window.open` under a "Post in this Group" CTA, so only an http(s)
 * Facebook link counts as a group. The recommendations are generated, so a link to another host, or
 * an empty or non-web link, is dropped.
 */
function isFacebookGroupUrl(url: string): boolean {
	try {
		const { protocol, hostname } = new URL(url);
		return (protocol === 'https:' || protocol === 'http:') && FACEBOOK_HOSTS.test(hostname);
	} catch {
		return false;
	}
}

/** A failed fetch throws instead of producing a value, so react-query can retry it. */
export type FacebookPostDataResult =
	| {
			readonly kind: 'ready';
			readonly caption: string;
			readonly groups: readonly FacebookGroupRecommendation[];
			readonly recommendationsStatus: ShareRecommendationsStatus;
	  }
	| {
			readonly kind: 'not-ready';
			readonly reason: 'no-caption' | 'no-groups';
			readonly recommendationsStatus: ShareRecommendationsStatus;
	  };

/** Thrown errors report only on the final attempt, so a successful retry never reports a transient blip. */
export type GetFacebookPostDataOptions = {
	isFinalRetryAttempt?: boolean;
};

type CaptionOutcome =
	| { readonly kind: 'caption'; readonly value: string }
	| { readonly kind: 'no-caption' }
	| { readonly kind: 'unexpected-error'; readonly message: string };

function extractCaption(data: NonNullable<PsfFacebookPostDataQuery['petitionBySlugOrId']>): CaptionOutcome {
	const { readyToSharePostsV2: result } = data;

	if (result.__typename === 'ReadyToSharePostsFailureV2') {
		if (result.reason === 'UNEXPECTED_ERROR') return { kind: 'unexpected-error', message: result.message };
		// RESOURCE_NOT_FOUND and UNRECOVERABLE_ERROR are expected, so they are not reported.
		return { kind: 'no-caption' };
	}

	const post = result.posts.find(
		(
			candidate,
		): candidate is Extract<(typeof result.posts)[number], { __typename: 'ReadyToShareFacebookGroupsPost' }> =>
			candidate.__typename === 'ReadyToShareFacebookGroupsPost',
	);
	if (!post?.bodyWithoutLink) return { kind: 'no-caption' };
	return { kind: 'caption', value: post.bodyWithoutLink };
}

function getThrownGqlErrorFailureReason(err: GQLResponseError): 'SERVER_ERROR' | 'GQL_ERROR' | 'NULL_DATA' {
	if (err instanceof GQLResponseServerError) return 'SERVER_ERROR';
	return err.errors.length === 0 ? 'NULL_DATA' : 'GQL_ERROR';
}

function buildResult(
	utilityContext: UtilityContext,
	variables: PsfFacebookPostDataQueryVariables,
	data: NonNullable<PsfFacebookPostDataQuery['petitionBySlugOrId']>,
	shareRecommendations: PsfFacebookPostDataQuery['shareRecommendations'],
): FacebookPostDataResult {
	const { status: recommendationsStatus } = shareRecommendations;
	// The URL is each card's key and selection identity, so a repeated URL keeps only its first group.
	const seenUrls = new Set<string>();
	const facebookGroups = shareRecommendations.facebook
		.map((group) => ({ ...group, url: group.url.trim() }))
		.filter(({ url }) => {
			if (!isFacebookGroupUrl(url) || seenUrls.has(url)) return false;
			seenUrls.add(url);
			return true;
		});
	const captionOutcome = extractCaption(data);

	if (captionOutcome.kind === 'unexpected-error') {
		reportFailure(utilityContext, variables, 'UNEXPECTED_ERROR', {
			error: new Error(FAILURE_MESSAGE),
			params: { message: captionOutcome.message },
		});
		return { kind: 'not-ready', reason: 'no-caption', recommendationsStatus };
	}
	if (captionOutcome.kind === 'no-caption') {
		return { kind: 'not-ready', reason: 'no-caption', recommendationsStatus };
	}
	if (facebookGroups.length === 0) {
		return { kind: 'not-ready', reason: 'no-groups', recommendationsStatus };
	}

	return {
		kind: 'ready',
		caption: captionOutcome.value,
		groups: facebookGroups.map((group) => ({
			name: group.name,
			url: group.url,
			imageUrl: group.metadata.imageUrl,
		})),
		recommendationsStatus,
	};
}

function reportFailure(
	utilityContext: UtilityContext,
	variables: PsfFacebookPostDataQueryVariables,
	failureReason: string,
	{ error, params = {} }: { error: Error; params?: Record<string, unknown> },
): void {
	withScope((scope) => {
		scope.setTags({
			feature: 'psf_facebook_post_sheet_eligibility',
			'page.section': 'psf_promote_or_share.facebook_post_data',
			'gql.operation': 'PsfFacebookPostData',
			'failure.reason': failureReason,
		});
		scope.setContext('petition', { slugOrId: variables.slugOrId });
		scope.setFingerprint(['psf-facebook-post-data-fetch-failure', failureReason]);
		void utilityContext.errorReporter.report({ error, params: { variables, ...params } });
	});
}

export async function getFacebookPostData(
	utilityContext: UtilityContext,
	variables: PsfFacebookPostDataQueryVariables,
	{ isFinalRetryAttempt = true }: GetFacebookPostDataOptions = {},
): Promise<FacebookPostDataResult> {
	const {
		gql: { fetch },
	} = utilityContext;

	/** The cause keeps the original error and its requestId in Sentry. */
	const reportAndThrow = (failureReason: string, cause?: unknown, params?: Record<string, unknown>): never => {
		const error = new Error(FAILURE_MESSAGE, cause === undefined ? undefined : { cause });
		if (isFinalRetryAttempt) {
			reportFailure(utilityContext, variables, failureReason, { error, params });
		}
		throw error;
	};

	let data: PsfFacebookPostDataQuery;
	try {
		({ data } = await fetch<PsfFacebookPostDataQuery, PsfFacebookPostDataQueryVariables>({
			query: PsfFacebookPostData,
			variables,
			rejectOnError: true,
		}));
	} catch (err) {
		// The gql client reports network failures itself, and a PerimeterX challenge is a bot check, not a
		// failure. It reports 5xx and body errors only as warnings, so this layer reports them as errors.
		if (!(err instanceof GQLResponseError) || err instanceof GQLResponsePerimeterXChallengeError) throw err;
		return reportAndThrow(getThrownGqlErrorFailureReason(err), err, { errors: err.errors });
	}

	if (!data.petitionBySlugOrId) {
		return reportAndThrow('NULL_DATA');
	}

	return buildResult(utilityContext, variables, data.petitionBySlugOrId, data.shareRecommendations);
}
